/**
 * 017: structure copied from
 * vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/category/category-list/table.tsx
 *
 * Only the data, columns and labels are ours. Reuses the same source brand's own List.tsx cites
 * (screen-map.md row 10).
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useLoaderData, useOutletContext, useSearchParams, type LoaderFunctionArgs } from "react-router-dom";
import { PiPlusBold as Plus } from "react-icons/pi";
import { PagedTable } from "../../../components/table/data-table";
import { ListHeader } from "../../../components/table/list-header";
import { activeStatusOptions, TableSearch } from "../../../components/table/toolbar";
import { categoryColumns } from "./columns";
import { deactivateCategory, listCategoryTree, reactivateCategory, type Category } from "../../../lib/services/catalog";
import { can } from "../../../lib/permissions";
import { activeViewParams, hrefWith, readActiveView } from "../../../lib/paging";
import type { PermissionState, AccountSummary } from "../../../types";
import { EmptyState } from "@steelyard/theme";
import { Button } from "rizzui";

/**
 * `GET /categories/tree` returns the whole tree, unpaginated, parents-before-children (FR-008) —
 * no `page` to read from the URL, unlike `brandsListLoader`. `activeOnly` is the only query param
 * the tree endpoint accepts, so Inactive is the full tree's rows the API marked `isActive: false`,
 * in the tree's own order. Every count here is a count of a whole tree the API returned — the All
 * and Active trees' own `totalCount`s, and the inactive rows of the full one — never "all − active".
 */
/**
 * The tree endpoint's own order is breadth-first by depth — every depth-0 row, then every depth-1
 * row, then depth-2, and so on — not a depth-first walk. So a depth-1 row does not sit next to its
 * real parent in that array; it sits next to whichever other depth-1 row happened to land at the
 * same position in that pass. Rendering straight from that order (as the table did before this)
 * visually pairs a category with the WRONG parent — an unrelated category's child looks like it
 * belongs to the row directly above it. This rebuilds the display order from the API's own
 * `parentId` field: a depth-first walk, each parent immediately followed by all of its own
 * descendants, siblings kept in the API's relative order — which is what "parents before children"
 * (FR-008) has to mean for a table that shows nesting by row adjacency. `depth` itself is never
 * recomputed, only the row order; a category whose parent isn't in this result (filtered out by the
 * active-only view) renders as its own root rather than being silently dropped.
 */
export function treeOrder(items: Category[]): Category[] {
  const ids = new Set(items.map((c) => c.id));
  const childrenOf = new Map<string | null, Category[]>();
  for (const item of items) {
    const parentKey = item.parentId && ids.has(item.parentId) ? item.parentId : null;
    if (!childrenOf.has(parentKey)) childrenOf.set(parentKey, []);
    childrenOf.get(parentKey)!.push(item);
  }
  const ordered: Category[] = [];
  const visit = (parentId: string | null) => {
    for (const item of childrenOf.get(parentId) ?? []) {
      ordered.push(item);
      visit(item.id);
    }
  };
  visit(null);
  return ordered;
}
export async function categoriesListLoader({ request }: LoaderFunctionArgs) {
  const url = new URL(request.url);
  const view = readActiveView(url.searchParams);
  // The tree the view shows must load; the other one only feeds a count, and may fail on its own.
  const [every, active] = await Promise.all([
    view === "active" ? listCategoryTree(false).catch(() => null) : listCategoryTree(false),
    view === "active" ? listCategoryTree(true) : listCategoryTree(true).catch(() => null),
  ]);
  const shown = view === "active" ? active : every;
  if (!shown) throw new Error("The API returned no body for the category tree.");
  const inactiveItems = every ? every.items.filter((category) => !category.isActive) : [];
  return {
    result: view === "inactive" ? { items: inactiveItems, page: 1, pageSize: inactiveItems.length, totalCount: inactiveItems.length } : shown,
    view,
    counts: {
      all: every ? Number(every.totalCount) : null,
      active: active ? Number(active.totalCount) : null,
      inactive: every ? inactiveItems.length : null,
    },
  };
}

export default function CategoriesList() {
  const { result, view, counts } = useLoaderData() as Awaited<ReturnType<typeof categoriesListLoader>>;
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const [searchParams] = useSearchParams();
  const canWrite = can(permissionState, "catalogue.administer");
  // The tree is one unpaginated response, so `PagedTable` pages it in memory (10 rows by default,
  // `?pageSize=` otherwise) — and the search box below searches every category, not just the rows
  // on screen, safe by construction unlike a server-paged list (see `orders/filters.tsx`). Note:
  // the search only ever sees `visibleItems` below, so a collapsed subtree's names don't match —
  // expand first, same trade-off any tree-plus-search view makes.

  // Reordered so a parent is immediately followed by its own descendants — see `treeOrder`'s own
  // comment for why the API's raw order can't be rendered by adjacency as-is.
  const orderedItems = useMemo(() => treeOrder(result.items), [result.items]);

  // Which categories have at least one row beneath them — a leaf gets no twisty at all.
  const parentIds = useMemo(
    () => new Set(orderedItems.map((c) => c.parentId).filter((id): id is string => id !== null)),
    [orderedItems],
  );

  // Collapsed by default, so the screen opens as a short list of top-level categories rather than
  // every sub-category at once — expand one to drill in. Reset whenever the loader hands back a
  // new tree (a status-filter switch, or a revalidation after an edit).
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set(parentIds));
  useEffect(() => setCollapsed(new Set(parentIds)), [result, parentIds]);

  const toggle = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // A collapsed parent hides every row beneath it (deeper depth) up to the next row back at its own
  // depth or shallower — one pass over `orderedItems`, which is genuinely depth-first now, so a
  // "deeper row right after this one" really is its child, not an unrelated category's.
  const visibleItems = useMemo(() => {
    let hideBelowDepth: number | null = null;
    const visible: Category[] = [];
    for (const category of orderedItems) {
      if (hideBelowDepth !== null && category.depth > hideBelowDepth) continue;
      hideBelowDepth = null;
      visible.push(category);
      if (collapsed.has(category.id)) hideBelowDepth = category.depth;
    }
    return visible;
  }, [orderedItems, collapsed]);

  return (
    <div className="flex flex-col gap-6">
      <ListHeader
        title="Categories"
        count={result.totalCount}
        description="Every category and sub-category a product can be filed under, listed parents before children."
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Catalogue" }, { name: "Categories" }]}
      />

      <PagedTable<Category>
        actions={
          canWrite ? (
            <Link to="/catalogue/categories/new">
              <Button as="span">
                <Plus aria-hidden className="me-1.5 size-4" />
                New category
              </Button>
            </Link>
          ) : null
        }
        // `visibleItems` drops whatever a collapsed parent hides; `totalCount` matches it so
        // `PagedTable` still pages this in memory instead of mistaking the shorter list for a
        // server page (its own `items.length < totalCount` check). `ListHeader` above still shows
        // `result.totalCount` — the real count of every category, collapsed or not.
        result={{ ...result, items: visibleItems, totalCount: visibleItems.length }}
        caption="Categories, indented by depth, with code and status"
        columns={categoryColumns({
          hasChildren: (id) => parentIds.has(id),
          isCollapsed: (id) => collapsed.has(id),
          onToggle: toggle,
          setStatus: canWrite ? (category, active) => (active ? reactivateCategory : deactivateCategory)(category.id) : undefined,
        })}
        getRowId={(row) => row.id}
        // Every non-root row gets a light tint so a parent's visible children read as one indented
        // group beneath it, not a flat list with slightly different left padding — same generic
        // prop BR-07 uses for its own row emphasis, no new plumbing. `gray-100` mirrors per theme
        // (globals.css), so this needs no separate dark: class to stay subtle in both.
        rowClassName={(row) => (row.depth > 0 ? "bg-gray-100" : undefined)}
        status={{
          label: "Status",
          options: activeStatusOptions(view, (next) => hrefWith("/catalogue/categories", searchParams, activeViewParams(next)), counts),
        }}
        search={(table) => <TableSearch table={table} placeholder="Search categories…" />}
        clear={{ active: view !== "active", href: hrefWith("/catalogue/categories", searchParams, activeViewParams("active")) }}
        empty={
          <EmptyState
            title={view === "active" ? "No active categories" : view === "inactive" ? "No inactive categories" : "No categories yet"}
            action={
              canWrite && view === "all" ? (
                <Link to="/catalogue/categories/new">
                  <Button as="span" size="sm">
                    <Plus aria-hidden />
                    New category
                  </Button>
                </Link>
              ) : view !== "all" ? (
                <Link to="/catalogue/categories?all=1">
                  <Button as="span" variant="outline" size="sm">
                    Show every category
                  </Button>
                </Link>
              ) : null
            }
          >
            {view === "active"
              ? "No category is active right now — every one has been deactivated."
              : view === "inactive"
                ? "Every category is active. A deactivated one stays listed here, never removed."
                : "Create the first one and it becomes selectable on every product."}
          </EmptyState>
        }
      />
    </div>
  );
}
